/**
 * SDMS Template Builder — New / Edit Formatting Rule (shared <AppDialog>), opened from the
 * Conditional Formatting Rules Manager. Edits the rule's range, definition and format;
 * nothing is written until the template is saved.
 */
import { useEffect, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { absoluteSqref, CF_OPERATORS, normalizeSqref, type CfDefinition } from './cfModel';

type RuleType = 'cellIs' | 'containsText' | 'expression';

export function ConditionalFormatDialog({ open, mode, sheetName, initial, onDone, onClose }: {
  open: boolean;
  mode: 'new' | 'edit';
  sheetName: string;
  /** the rule being edited, or the defaults for a new one (its range = the selection) */
  initial: CfDefinition;
  onDone: (def: CfDefinition) => void;
  onClose: () => void;
}) {
  const [range, setRange] = useState('');
  const [type, setType] = useState<RuleType>('cellIs');
  const [operator, setOperator] = useState('greaterThan');
  const [v1, setV1] = useState('');
  const [v2, setV2] = useState('');
  const [text, setText] = useState('');
  const [fontColor, setFontColor] = useState('#9C0006');
  const [useFont, setUseFont] = useState(true);
  const [fillColor, setFillColor] = useState('#FFC7CE');
  const [useFill, setUseFill] = useState(true);
  const [bold, setBold] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRange(initial.range ? absoluteSqref(initial.range) : '');
    setType((['cellIs', 'containsText', 'expression'].includes(initial.type) ? initial.type : 'cellIs') as RuleType);
    setOperator(initial.operator ?? 'greaterThan');
    setV1(initial.formula1 ?? '');
    setV2(initial.formula2 ?? '');
    setText(initial.text ?? '');
    setUseFont(mode === 'new' || !!initial.fontColor);
    setFontColor(initial.fontColor ?? '#9C0006');
    setUseFill(mode === 'new' || !!initial.fillColor);
    setFillColor(initial.fillColor ?? '#FFC7CE');
    setBold(initial.bold);
    setProblem(null);
  }, [open, initial, mode]);

  const needsTwo = operator === 'between' || operator === 'notBetween';

  const done = () => {
    const sqref = normalizeSqref(range);
    if (!sqref) return setProblem('Enter the cells, e.g. =$Q$7 or =$Q$7:$Q$20,$S$7.');
    if (type === 'cellIs' && (!v1.trim() || (needsTwo && !v2.trim()))) return setProblem(needsTwo ? 'Enter both values.' : 'Enter the value to compare with.');
    if (type === 'containsText' && !text.trim()) return setProblem('Enter the text to look for.');
    if (type === 'expression' && !v1.trim()) return setProblem('Enter the formula.');
    if (!useFont && !useFill && !bold) return setProblem('Choose how matching cells should look.');
    onDone({
      range: sqref, type, operator: type === 'cellIs' ? operator : null,
      formula1: type === 'containsText' ? null : v1.trim(),
      formula2: type === 'cellIs' && needsTwo ? v2.trim() : null,
      text: type === 'containsText' ? text.trim() : null,
      fontColor: useFont ? fontColor.toUpperCase() : null,
      fillColor: useFill ? fillColor.toUpperCase() : null,
      bold,
    });
  };

  return (
    <AppDialog open={open} title={`${mode === 'new' ? 'New' : 'Edit'} Formatting Rule — ${sheetName}`} icon="ti-color-filter"
      width={560} onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="ca-btn ca-btn-solid" onClick={done}>OK</button>
      </>}>
      <div className="sd-grid sd-grid--side sd-grid--single stb-cf-form">
        <div className="sd-row">
          <label className="sd-label" htmlFor="cf-range">Applies to<span className="req">*</span></label>
          <input id="cf-range" className="sd-input" value={range} onChange={e => setRange(e.target.value)} placeholder="=$Q$7:$Q$20" />
        </div>
        <div className="sd-row">
          <label className="sd-label" htmlFor="cf-type">Format only cells with</label>
          <select id="cf-type" className="sd-input" value={type} onChange={e => setType(e.target.value as RuleType)}>
            <option value="cellIs">Cell value</option>
            <option value="containsText">Specific text</option>
            <option value="expression">A formula that is true</option>
          </select>
        </div>
        {type === 'cellIs' && (<>
          <div className="sd-row">
            <label className="sd-label" htmlFor="cf-op">Comparison</label>
            <select id="cf-op" className="sd-input" value={operator} onChange={e => setOperator(e.target.value)}>
              {CF_OPERATORS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="sd-row">
            <label className="sd-label" htmlFor="cf-v1">Value<span className="req">*</span></label>
            <input id="cf-v1" className="sd-input" value={v1} onChange={e => setV1(e.target.value)} placeholder="120 or =$O7" />
          </div>
          {needsTwo && (
            <div className="sd-row">
              <label className="sd-label" htmlFor="cf-v2">And<span className="req">*</span></label>
              <input id="cf-v2" className="sd-input" value={v2} onChange={e => setV2(e.target.value)} />
            </div>
          )}
        </>)}
        {type === 'containsText' && (
          <div className="sd-row">
            <label className="sd-label" htmlFor="cf-text">Containing<span className="req">*</span></label>
            <input id="cf-text" className="sd-input" value={text} onChange={e => setText(e.target.value)} placeholder="Fail" />
          </div>
        )}
        {type === 'expression' && (
          <div className="sd-row">
            <label className="sd-label" htmlFor="cf-expr">Formula<span className="req">*</span></label>
            <input id="cf-expr" className="sd-input" value={v1} onChange={e => setV1(e.target.value)} placeholder='=G8<>""' />
          </div>
        )}
        <div className="sd-row">
          <span className="sd-label">Font colour</span>
          <span className="stb-cf-pick">
            <input type="checkbox" checked={useFont} onChange={e => setUseFont(e.target.checked)} aria-label="Use font colour" />
            <input type="color" value={fontColor} disabled={!useFont} onChange={e => setFontColor(e.target.value)} aria-label="Font colour" />
            <label><input type="checkbox" checked={bold} onChange={e => setBold(e.target.checked)} /> Bold</label>
          </span>
        </div>
        <div className="sd-row">
          <span className="sd-label">Fill colour</span>
          <span className="stb-cf-pick">
            <input type="checkbox" checked={useFill} onChange={e => setUseFill(e.target.checked)} aria-label="Use fill colour" />
            <input type="color" value={fillColor} disabled={!useFill} onChange={e => setFillColor(e.target.value)} aria-label="Fill colour" />
          </span>
        </div>
        <div className="sd-row">
          <span className="sd-label">Preview</span>
          <span className="stb-cf-sample" style={{ color: useFont ? fontColor : undefined, background: useFill ? fillColor : undefined, fontWeight: bold ? 700 : undefined }}>AaBbCcYyZz</span>
        </div>
        {problem && <div className="sd-row"><span /><span className="sd-field-error">{problem}</span></div>}
      </div>
    </AppDialog>
  );
}
